import update from 'immutability-helper';
import { createReducer } from '../../utils/redux/reducers.utils';
import { getParentIdByPath } from '../../utils/Utils';
import {
  AUTH_LOGOUT,
  INITIAL_STATE,
  KNOWLEDGE_CREATE_CATEGORY,
  KNOWLEDGE_CREATE_ITEM,
  KNOWLEDGE_DELETE_CATEGORY,
  KNOWLEDGE_DELETE_ITEM,
  KNOWLEDGE_UPDATE_CATEGORY,
  KNOWLEDGE_UPDATE_CONTENT,
  KNOWLEDGE_UPDATE_ITEM,
  KNOWLEDGE_UPDATE_ITEMS_ORDER,
  KNOWLEDGE_UPDATE_SUBCATEGORIES,
  KNOWLEDGE_USE_CACHE,
  APPLICATION_WIPE_DATA,
  KNOWLEDGE_CREATE_LINK,
  KNOWLEDGE_DELETE_LINK,
  KNOWLEDGE_UPDATE_LINK,
  KNOWLEDGE_MOVE_LINK,
  KNOWLEDGE_DASHBOARD_UPDATE_CONTENT,
  KNOWLEDGE_UPDATE_ITEM_CONTENT,
  KNOWLEDGE_UPDATE_KEYWORDS,
  KNOWLEDGE_CATALOG_DOWNLOAD_START,
  KNOWLEDGE_CATALOG_DOWNLOAD_END,
  KNOWLEDGE_CATALOG_REFETCH_REQUESTED,
  KNOWLEDGE_CATALOG_DROP,
  KNOWLEDGE_UPDATE_CATEGORIES,
  KNOWLEDGE_CATEGORIES_FETCH_START,
  KNOWLEDGE_CATEGORIES_FETCH_END,
  KNOWLEDGE_CATEGORIES_REFETCH_REQUESTED,
} from '../actions';
import _ from 'lodash';

const initialState = {
  items: null,
  itemsOpenEventsForOrganization: null,
  itemsOpenEventsForUser: null,
  itemsById: null,
  categories: null,
  categoriesById: null,
  lastUpdated: null,
  previousLastUpdated: null,
  isReady: false,
  // Id of the catalog download in progress, null when none. Lives in the slice
  // so a wipe or logout clears it with everything else.
  catalogRequestId: null,
  // A refresh asked for while a download ran: the running one may predate the
  // change, so one more download follows it (bursts collapse to two).
  catalogRefetchPending: false,
  // Id of the latest categories-only fetch. Responses race and nothing cancels
  // them, so only the newest one may land; a wipe clears it mid-flight.
  categoriesRequestId: null,
  categoriesRefetchPending: false,
};

const startCategoriesFetch = (state, { requestId }) =>
  update(state, {
    categoriesRequestId: { $set: requestId },
    categoriesRefetchPending: { $set: false },
  });

const requestCategoriesRefetch = state =>
  state.categoriesRequestId ? update(state, { categoriesRefetchPending: { $set: true } }) : state;

// A fetch that failed must not keep the next trigger from fetching again.
const endCategoriesFetch = (state, { requestId }) =>
  state.categoriesRequestId === requestId
    ? update(state, { categoriesRequestId: { $set: null } })
    : state;

// Also supersedes a categories-only fetch in flight: its tree would be older
// than the download's and must not land on top of it.
const startCatalogDownload = (state, { requestId }) =>
  update(state, {
    catalogRequestId: { $set: requestId },
    catalogRefetchPending: { $set: false },
    categoriesRequestId: { $set: null },
    categoriesRefetchPending: { $set: false },
  });

const endCatalogDownload = (state, { requestId }) =>
  state.catalogRequestId === requestId
    ? update(state, { catalogRequestId: { $set: null } })
    : state;

const requestCatalogRefetch = state =>
  state.catalogRequestId ? update(state, { catalogRefetchPending: { $set: true } }) : state;

// A client that reads item rows on demand drops the rows a previous session
// (or an older build) left behind; the tree and the readiness flags stay.
// `keepIds` are the rows to keep: items whose file is downloaded for offline use.
const dropCatalog = (state, { keepIds = [] } = {}) => {
  const kept = _.pick(state.itemsById, keepIds);

  return update(state, {
    items: { $set: null },
    itemsById: { $set: _.isEmpty(kept) ? null : kept },
    catalogRequestId: { $set: null },
    catalogRefetchPending: { $set: false },
  });
};

const updateKnowledge = (state, { items, itemsById, categories, categoriesById, requestId }) => {
  // A result from a download that was wiped mid-flight belongs to the old session.
  if (requestId !== undefined && requestId !== state.catalogRequestId) return state;

  const mergedItems = items.map(item => {
    const existingItem = state.itemsById?.[item.id];
    if (!item.content && existingItem?.content) {
      return { ...item, content: existingItem.content };
    }
    return item;
  });

  const mergedItemsById = Object.fromEntries(
    Object.entries(itemsById).map(([id, item]) => {
      const existingItem = state.itemsById?.[id];
      if (!item.content && existingItem?.content) {
        return [id, { ...item, content: existingItem.content }];
      }
      return [id, item];
    }),
  );

  return update(state, {
    $merge: {
      items: mergedItems,
      itemsById: mergedItemsById,
      categories,
      categoriesById,
      lastUpdated: Date.now(),
      previousLastUpdated: state.lastUpdated || Date.now(),
      isReady: true,
    },
  });
};

const updateCategories = (state, { categories, categoriesById, requestId }) => {
  if (requestId !== state.categoriesRequestId) return state;

  return update(state, {
    $merge: {
      categories,
      categoriesById,
      lastUpdated: Date.now(),
      previousLastUpdated: state.lastUpdated || Date.now(),
      isReady: true,
      categoriesRequestId: null,
    },
  });
};

const updateKnowledgeDashboard = (
  state,
  { itemsOpenEventsForOrganization, itemsOpenEventsForUser },
) =>
  update(state, {
    $merge: {
      itemsOpenEventsForOrganization,
      itemsOpenEventsForUser,
      isReady: true,
    },
  });

// An item may reach the store before the catalog holds it (opened mid-download,
// or not loaded at all), so writes merge into `itemsById` and only touch `items`
// where the row already exists.
const mergeItemSpec = (state, item) => {
  const index = state.items?.findIndex(i => i.id === item.id) ?? -1;

  return {
    ...(index !== -1 && { items: { [index]: { $merge: item } } }),
    itemsById: {
      $apply: byId => ({ ...byId, [item.id]: { ...byId?.[item.id], ...item } }),
    },
  };
};

// No `lastUpdated` bump: opening an item is not a catalog change, and the bump
// remounted the mobile tree and refetched the dashboard widgets on every open.
const updateItemContent = (state, { item }) => update(state, mergeItemSpec(state, item));

const updateCategory = (state, change) => {
  let { id } = change;
  let index = state.categories.findIndex(category => category.id === id);

  return update(state, {
    categories: {
      [index]: { $merge: change },
    },
    categoriesById: {
      [id]: { $merge: change },
    },
    lastUpdated: { $set: Date.now() },
  });
};

const deleteCategory = (state, { id }) => {
  let index = state.categories.findIndex(category => category.id === id);
  let cat = state.categoriesById[id];

  let parentId = getParentIdByPath(cat.path);

  let updateAction = {
    categories: { $splice: [[index, 1]] },
    categoriesById: {
      $apply: function (obj) {
        delete obj[id];
        return obj;
      },
    },
    lastUpdated: { $set: Date.now() },
  };

  if (parentId) {
    let indexParent = state.categories.findIndex(category => category.id === parentId);
    let currentIndex = state.categories[indexParent].subcategories.findIndex(i => i === id);
    let action = {
      subcategories: {
        $splice: [[currentIndex, 1]],
      },
    };

    updateAction.categories[indexParent] = action;
    updateAction.categoriesById[parentId] = action;
  }

  return update(state, updateAction);
};

const createCategory = (state, { path, category }) => {
  let parentId = getParentIdByPath(path);

  let updateAction = {
    categories: {
      $push: [category],
    },
    categoriesById: {
      [category.id]: { $set: category },
    },
    lastUpdated: { $set: Date.now() },
  };

  if (parentId) {
    let indexParent = state.categories.findIndex(category => category.id === parentId);
    let action = {
      subcategories: { $push: [category.id] },
    };
    updateAction.categories[indexParent] = action;
    updateAction.categoriesById[parentId] = action;
  }

  return update(state, updateAction);
};

const updateSubcategories = (state, { id, categoryId, index }) => {
  let catIndex = state.categories.findIndex(category => category.id === id);
  let currentIndex = state.categories[catIndex].subcategories.findIndex(i => i === categoryId);
  let item = state.categories[catIndex].subcategories[currentIndex];

  return update(state, {
    categories: {
      [catIndex]: {
        subcategories: {
          $splice: [
            [currentIndex, 1],
            [index, 0, item],
          ],
        },
      },
    },
    categoriesById: {
      [id]: {
        subcategories: {
          $splice: [
            [currentIndex, 1],
            [index, 0, item],
          ],
        },
      },
    },

    lastUpdated: { $set: Date.now() },
  });
};

const updateItemsOrder = (state, { id, itemId, index }) => {
  let catIndex = state.categories.findIndex(category => category.id === id);
  const { knowledgeItems } = state.categories[catIndex];
  const currentIndex = knowledgeItems.findIndex(item => item.id === itemId);

  let item = state.categories[catIndex].knowledgeItems[currentIndex];

  return update(state, {
    categories: {
      [catIndex]: {
        knowledgeItems: {
          $splice: [
            [currentIndex, 1],
            [index, 0, item],
          ],
        },
      },
    },
    categoriesById: {
      [id]: {
        knowledgeItems: {
          $splice: [
            [currentIndex, 1],
            [index, 0, item],
          ],
        },
      },
    },

    lastUpdated: { $set: Date.now() },
  });
};

const createItem = (state, { item, categoryId }) => {
  const index = state.categories?.findIndex(category => category.id === categoryId) ?? -1;
  const link = { id: item.id };

  return update(state, {
    items: { $apply: items => (items ? [...items, item] : items) },
    itemsById: { $apply: byId => ({ ...byId, [item.id]: item }) },
    ...(index !== -1 && {
      categories: { [index]: { knowledgeItems: { $push: [link] } } },
      categoriesById: { [categoryId]: { knowledgeItems: { $push: [link] } } },
    }),
    lastUpdated: { $set: Date.now() },
  });
};

const updateItem = (state, { item }) => {
  const existingItem = state.itemsById?.[item.id];

  const itemToMerge =
    !item.content && existingItem?.content ? { ...item, content: existingItem.content } : item;

  return update(state, {
    ...mergeItemSpec(state, itemToMerge),
    lastUpdated: { $set: Date.now() },
  });
};

const updateKeywords = (state, { id, keywords }) => {
  const existingKeywords = state.itemsById?.[id]?.keywords ?? [];

  return update(state, {
    ...mergeItemSpec(state, { id, keywords: _.uniq([...existingKeywords, ...keywords]) }),
    lastUpdated: { $set: Date.now() },
  });
};

const deleteItem = (state, { id }) => {
  const index = state.items?.findIndex(i => i.id === id) ?? -1;

  return update(state, {
    ...(index !== -1 && { items: { $splice: [[index, 1]] } }),
    itemsById: { $apply: byId => (byId ? _.omit(byId, id) : byId) },
    lastUpdated: { $set: Date.now() },
  });
};

const createLink = (state, { items, categoryId }) => {
  const index = state.categories.findIndex(category => category.id === categoryId);

  const linkedItems = items.map(item => ({
    id: item.itemId,
    linked: true,
    linkOwnerIds: item.ownerIds,
  }));

  return update(state, {
    categories: {
      [index]: { knowledgeItems: { $push: linkedItems } },
    },
    categoriesById: {
      [categoryId]: { knowledgeItems: { $push: linkedItems } },
    },
    lastUpdated: { $set: Date.now() },
  });
};

const deleteLink = (state, { id, categoryId }) => {
  const catIndex = state.categories.findIndex(category => category.id === categoryId);

  const itemIndex = state.categories[catIndex]?.knowledgeItems.findIndex(item => item.id === id);

  return update(state, {
    categories: {
      [catIndex]: { knowledgeItems: { $splice: [[itemIndex, 1]] } },
    },
    categoriesById: {
      [categoryId]: { knowledgeItems: { $splice: [[itemIndex, 1]] } },
    },
    lastUpdated: { $set: Date.now() },
  });
};

const updateLink = (state, { id, categoryId, linkOwnerIds }) => {
  const catIndex = state.categories.findIndex(category => category.id === categoryId);

  const itemIndex = state.categories[catIndex]?.knowledgeItems.findIndex(item => item.id === id);

  return update(state, {
    categories: {
      [catIndex]: { knowledgeItems: { [itemIndex]: { $merge: { linkOwnerIds } } } },
    },
    categoriesById: {
      [categoryId]: { knowledgeItems: { [itemIndex]: { $merge: { linkOwnerIds } } } },
    },
    lastUpdated: { $set: Date.now() },
  });
};

const moveLink = (state, { id, categoryId, newCategoryId }) => {
  const catIndex = state.categories.findIndex(category => category.id === categoryId);
  const newCatIndex = state.categories.findIndex(category => category.id === newCategoryId);

  let itemIndex;

  const item = state.categories[catIndex]?.knowledgeItems.find((item, index) => {
    if (item.id === id) {
      itemIndex = index;
      return item;
    }
    return null;
  });

  return update(state, {
    categories: {
      [catIndex]: { knowledgeItems: { $splice: [[itemIndex, 1]] } },
      [newCatIndex]: { knowledgeItems: { $push: [item] } },
    },
    categoriesById: {
      [categoryId]: { knowledgeItems: { $splice: [[itemIndex, 1]] } },
      [newCategoryId]: { knowledgeItems: { $push: [item] } },
    },
    lastUpdated: { $set: Date.now() },
  });
};

const useCache = state =>
  update(state, {
    isReady: { $set: true },
  });

const setInitialState = (state, { knowledge = initialState }) =>
  update(state, {
    $merge: {
      ...knowledge,
      isReady: false,
      catalogRequestId: null,
      catalogRefetchPending: false,
      categoriesRequestId: null,
      categoriesRefetchPending: false,
    },
  });

const cleanData = (state, payload) => {
  return initialState;
};

const actionsRepo = {
  [INITIAL_STATE]: setInitialState,
  [KNOWLEDGE_UPDATE_CONTENT]: updateKnowledge,
  [KNOWLEDGE_UPDATE_CATEGORIES]: updateCategories,
  [KNOWLEDGE_CATEGORIES_FETCH_START]: startCategoriesFetch,
  [KNOWLEDGE_CATEGORIES_FETCH_END]: endCategoriesFetch,
  [KNOWLEDGE_CATEGORIES_REFETCH_REQUESTED]: requestCategoriesRefetch,
  [KNOWLEDGE_DASHBOARD_UPDATE_CONTENT]: updateKnowledgeDashboard,
  [KNOWLEDGE_UPDATE_ITEM_CONTENT]: updateItemContent,
  [KNOWLEDGE_UPDATE_CATEGORY]: updateCategory,
  [KNOWLEDGE_DELETE_CATEGORY]: deleteCategory,
  [KNOWLEDGE_CREATE_CATEGORY]: createCategory,
  [KNOWLEDGE_UPDATE_SUBCATEGORIES]: updateSubcategories,
  [KNOWLEDGE_CREATE_ITEM]: createItem,
  [KNOWLEDGE_UPDATE_ITEM]: updateItem,
  [KNOWLEDGE_UPDATE_KEYWORDS]: updateKeywords,
  [KNOWLEDGE_DELETE_ITEM]: deleteItem,
  [KNOWLEDGE_UPDATE_ITEMS_ORDER]: updateItemsOrder,
  [KNOWLEDGE_CREATE_LINK]: createLink,
  [KNOWLEDGE_DELETE_LINK]: deleteLink,
  [KNOWLEDGE_UPDATE_LINK]: updateLink,
  [KNOWLEDGE_MOVE_LINK]: moveLink,
  [KNOWLEDGE_USE_CACHE]: useCache,
  [KNOWLEDGE_CATALOG_DOWNLOAD_START]: startCatalogDownload,
  [KNOWLEDGE_CATALOG_DOWNLOAD_END]: endCatalogDownload,
  [KNOWLEDGE_CATALOG_REFETCH_REQUESTED]: requestCatalogRefetch,
  [KNOWLEDGE_CATALOG_DROP]: dropCatalog,
  [APPLICATION_WIPE_DATA]: cleanData,
  [AUTH_LOGOUT]: cleanData,
};

export default createReducer({ initialState, actionsRepo });
